/**
 * Notification Center / Action Queue Component
 * 
 * Displays pending actions and notifications in a dropdown:
 * - New intake responses
 * - Pending prescriptions
 * - IntelliHealth opportunities
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  ClipboardList,
  FileText,
  Sparkles,
  Clock,
  CheckCircle2,
  ChevronRight,
  RefreshCw,
  Loader2,
  X,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  getRecentCompletions,
  getIntakeMetrics,
  RecentCompletion,
  getTherapyCategoryLabel
} from '@/services/intakeService';
import { useClinic } from '@/contexts/ClinicContext';
import { useNotifications, Notification } from '@/contexts/NotificationContext';

interface NotificationItem {
  id: string;
  type: 'intake' | 'prescription' | 'intellihealth' | 'message';
  title: string;
  subtitle: string;
  timestamp: Date;
  read: boolean;
  action: string;
}

const NotificationCenter: React.FC = () => {
  const { clinic } = useClinic();
  const { notifications: systemNotifications, unreadCount: systemUnreadCount, markAsRead: markSystemRead } = useNotifications();
  const isGuided = clinic?.clinic_archetype === 'guided';
  const responseLabel = isGuided ? 'New Enrollment Response' : 'New Intake Response';
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [newResponsesCount, setNewResponsesCount] = useState(0);

  const loadNotifications = async () => {
    setIsLoading(true);
    try {
      // Load intake metrics and recent completions in parallel
      const [metrics, completions] = await Promise.all([
        getIntakeMetrics(
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          new Date().toISOString()
        ),
        getRecentCompletions(60)
      ]);

      const notificationItems: NotificationItem[] = [];

      // Add new intake responses
      if (completions.success && completions.data) {
        setNewResponsesCount(completions.data.length);
        completions.data.slice(0, 5).forEach((completion: RecentCompletion) => {
          notificationItems.push({
            id: `intake-${completion.id}`,
            type: 'intake',
            title: responseLabel,
            subtitle: `${completion.patientName} completed ${getTherapyCategoryLabel(completion.therapyCategory)}`,
            timestamp: new Date(completion.completedAt),
            read: false,
            action: '/practitioner-portal/intakes'
          });
        });
      }

      // Set pending count from metrics
      if (metrics.success && metrics.data) {
        setPendingCount(metrics.data.pending || 0);
      }

      // Add messaging notifications from the system notification context
      systemNotifications
        .filter((n: Notification) => n.type === 'PATIENT_MESSAGE_RECEIVED')
        .slice(0, 5)
        .forEach((n: Notification) => {
          notificationItems.push({
            id: `msg-${n.id}`,
            type: 'message',
            title: n.title,
            subtitle: n.message,
            timestamp: new Date(n.created_at),
            read: n.read,
            action: '/messaging',
          });
        });

      // Sort by timestamp descending
      notificationItems.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

      setNotifications(notificationItems);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    // Refresh every 2 minutes
    const interval = setInterval(loadNotifications, 120000);
    return () => clearInterval(interval);
  }, [systemNotifications]);

  const handleNotificationClick = (notification: NotificationItem) => {
    // Mark system notifications as read
    if (notification.type === 'message' && notification.id.startsWith('msg-')) {
      const systemId = notification.id.replace('msg-', '');
      markSystemRead(systemId);
    }
    navigate(notification.action);
    setIsOpen(false);
  };

  const totalUnread = notifications.filter(n => !n.read).length + systemUnreadCount;

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'intake':
        return <ClipboardList className="w-4 h-4 text-blue-500" />;
      case 'prescription':
        return <FileText className="w-4 h-4 text-amber-500" />;
      case 'intellihealth':
        return <Sparkles className="w-4 h-4 text-blue-500" />;
      case 'message':
        return <MessageSquare className="w-4 h-4 text-emerald-500" />;
      default:
        return <Bell className="w-4 h-4 text-slate-500" />;
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 rounded-lg hover:bg-slate-100"
        >
          <Bell className="h-5 w-5 text-slate-600" />
          {totalUnread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[10px] font-medium text-white">
              {totalUnread > 9 ? '9+' : totalUnread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0" 
        align="end"
        sideOffset={8}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-900">Notifications</h3>
            {totalUnread > 0 && (
              <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">
                {totalUnread} new
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => loadNotifications()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 text-slate-400 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Quick Summary */}
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
          <div className="grid grid-cols-2 gap-3">
            <div 
              className="flex items-center gap-2 p-2 rounded-lg bg-white border border-slate-200 cursor-pointer hover:border-blue-300 transition-colors"
              onClick={() => { navigate('/practitioner-portal/intakes'); setIsOpen(false); }}
            >
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900">{pendingCount}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </div>
            <div 
              className="flex items-center gap-2 p-2 rounded-lg bg-white border border-slate-200 cursor-pointer hover:border-blue-300 transition-colors"
              onClick={() => { navigate('/practitioner-portal/intakes'); setIsOpen(false); }}
            >
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900">{newResponsesCount}</p>
                <p className="text-xs text-slate-500">Responses</p>
              </div>
            </div>
          </div>
        </div>

        {/* Notification List */}
        <ScrollArea className="h-[280px]">
          {isLoading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center px-4">
              <Bell className="w-10 h-10 text-slate-200 mb-3" />
              <p className="text-sm text-slate-500">No new notifications</p>
              <p className="text-xs text-slate-400 mt-1">You're all caught up!</p>
            </div>
          ) : (
            <div className="py-2">
              {notifications.map((notification, index) => (
                <div key={notification.id}>
                  <div
                    className={`px-4 py-3 cursor-pointer transition-colors ${
                      notification.read ? 'bg-white' : 'bg-blue-50/50'
                    } hover:bg-slate-50`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {notification.title}
                          </p>
                          <span className="text-xs text-slate-400 flex-shrink-0">
                            {formatTimeAgo(notification.timestamp)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                          {notification.subtitle}
                        </p>
                      </div>
                    </div>
                  </div>
                  {index < notifications.length - 1 && (
                    <Separator className="mx-4" />
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-100">
          <Button
            variant="ghost"
            className="w-full justify-center text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            onClick={() => { navigate('/practitioner-portal/intakes'); setIsOpen(false); }}
          >
            View All Activity
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationCenter;
